/**
 * The frequency search's constants and arithmetic.
 *
 * FCC stores an assignment as `NUMERIC(12,5)` MHz — 462.56250 — while a radio, a
 * programming sheet and a service monitor all show three decimals. So the search is a
 * window (a centre plus a tolerance), not an equality, and both the box in Advanced
 * Filters and the page that owns its state need to agree on what the window is.
 *
 * Kept out of `components/search/SearchFilters.tsx` so that file exports components
 * and nothing else — the same reason `utils/facetOptions.ts` exists.
 *
 * **The default has a twin in `app/services/frequency_filter.py`.** The server
 * applies its own default when the tolerance is absent; this one only decides what
 * the select shows. If they ever disagree, the sentence under the box describes a
 * window nobody searched.
 */

/** Ceiling on a searchable frequency, in MHz — the top of the radio spectrum. */
export const MAX_FREQUENCY_MHZ = 300000

/** The default window, in kHz. Must match the server's own default. */
export const DEFAULT_FREQUENCY_TOLERANCE_KHZ = 1

/**
 * How close a match counts, as a fixed list rather than a number box.
 *
 * A list because these are the questions people actually ask — this channel, its
 * neighbours, anything in this part of the band — and because a value the server
 * would refuse then cannot be typed at all.
 *
 * **Labels are the numbers alone, on purpose.** They used to carry the
 * explanation too ("This channel, allowing for rounding (± 5 kHz)"), which made a
 * seven-row dropdown read as a wall of prose for a control most people never
 * touch. The sentence under the field already states the resulting window in MHz
 * to five decimals, which is both shorter to read and more precise than any
 * label could be — so the label only has to identify the option.
 */
export const FREQUENCY_TOLERANCES: { value: number; label: string }[] = [
  { value: 0, label: 'Exact' },
  { value: 1, label: '± 1 kHz' },
  { value: 5, label: '± 5 kHz' },
  { value: 12.5, label: '± 12.5 kHz' },
  { value: 100, label: '± 100 kHz' },
  { value: 1000, label: '± 1 MHz' },
  { value: 5000, label: '± 5 MHz' },
]

/** A frequency typed into the box, as a number — or null if it is not usable yet. */
export function parseFrequency(raw: string): number | null {
  const trimmed = raw.trim()
  if (!trimmed || !/^\d*\.?\d*$/.test(trimmed)) return null
  const value = Number(trimmed)
  if (!Number.isFinite(value) || value <= 0 || value > MAX_FREQUENCY_MHZ) return null
  return value
}

/**
 * The window a search will actually cover, at the five decimals FCC stores.
 *
 * Rounded outward, as the server does, so the sentence under the box never claims a
 * narrower window than the one being searched.
 */
export function frequencyWindow(centre: number, toleranceKhz: number): [string, string] {
  const step = 100000
  const half = toleranceKhz / 1000
  const lo = Math.max(0, Math.floor((centre - half) * step) / step)
  const hi = Math.ceil((centre + half) * step) / step
  return [lo.toFixed(5), hi.toFixed(5)]
}
